import { BaseAPI } from '../../../../shared/infra/api/BaseAPI';
import { APIResponse } from '../../../../shared/infra/api/APIResponse';
import { IAuthService } from '../../../users/services/authService';
import { Result } from '../../../../shared/core/Result';
import { right, left } from '../../../../shared/core/Either';
import {
  DocumentUploadRequestDTO,
  DocumentUploadResponseDTO,
  ImageUploadRequestDTO,
  ImageUploadResponseDTO,
  LogoUploadRequestDTO,
  LogoUploadResponseDTO,
} from '../dtos';
import { AxiosError, AxiosProgressEvent } from 'axios';

export interface IFileService {
  uploadCoverImage(
    imageUploadRequest: ImageUploadRequestDTO,
    cb: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<APIResponse<ImageUploadResponseDTO>>;
  uploadLogoImage(
    logoUploadRequest: LogoUploadRequestDTO,
    cb: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<APIResponse<LogoUploadResponseDTO>>;
  uploadDocument(
    documentUploadRequest: DocumentUploadRequestDTO,
    cb: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<APIResponse<DocumentUploadResponseDTO>>;
}

export class FileService extends BaseAPI implements IFileService {
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error(`Method not implemented. ${error.name}`);
  }
  constructor(authService: IAuthService) {
    super(authService);
  }

  public async uploadLogoImage(
    logoUploadRequest: LogoUploadRequestDTO,
    cb: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<APIResponse<LogoUploadResponseDTO>> {
    try {
      const formData = new FormData();
      formData.append('logo', logoUploadRequest.logo);

      const response = await this.post<LogoUploadResponseDTO>({
        url: '/v1/upload/community/logo',
        requiresToken: true,
        payload: formData,
        config: {
          onUploadProgress: cb,
          headers: {
            'content-type': 'multipart/form-data',
          },
        },
      });
      const logoResponse = response.data;

      return right(Result.ok<LogoUploadResponseDTO>(logoResponse));
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async uploadDocument(
    documentUploadRequest: DocumentUploadRequestDTO,
    cb: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<APIResponse<DocumentUploadResponseDTO>> {
    try {
      const formData = new FormData();
      formData.append('document', documentUploadRequest.document);

      const response = await this.post<DocumentUploadResponseDTO>({
        url: '/v1/upload/feedItem/document',
        requiresToken: true,
        payload: formData,
        config: {
          onUploadProgress: cb,
          headers: {
            'content-type': 'multipart/form-data',
          },
        },
      });
      const documentResponse = response.data;

      return right(Result.ok<DocumentUploadResponseDTO>(documentResponse));
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async uploadCoverImage(
    imageUploadRequest: ImageUploadRequestDTO,
    cb: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<APIResponse<ImageUploadResponseDTO>> {
    try {
      const formData = new FormData();

      formData.append('cover', imageUploadRequest.cover);
      if (imageUploadRequest.displayName) {
        formData.append('displayName', imageUploadRequest.displayName);
      }

      const response = await this.post<ImageUploadResponseDTO>({
        url: '/v1/upload/feedItem/cover',
        requiresToken: true,
        payload: formData,
        config: {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            cb(progressEvent);
          },
        },
      });
      const imageUpload = response.data;

      return right(Result.ok<ImageUploadResponseDTO>(imageUpload));
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }
}
